/**
 * Per-page `<head>` for pages that want to be found: the tab title, the search
 * snippet, the canonical address, a `noindex` for pages that should not be,
 * and structured data. Used by the public Games page (ADR-035); search
 * engines that run script read these from the live page, and the static
 * snapshots carry the same values from the same source (`@fias/public-site-ui`
 * `seo.ts`).
 *
 * Shares `document.title` with the unread indicator (`useUnreadIndicator`),
 * which owns only a `(N) ` prefix: a title set here keeps whatever prefix is
 * on the current title, and the indicator re-derives its base from whatever
 * title it finds. Everything this writes is put back on unmount — the shell's
 * baseline description returns, and added tags are removed.
 */
import { useEffect } from 'react';
import { stripTitleBadge } from './unread-indicator';
import { serializeJsonForScript } from '@fias/public-site-ui';

export interface PageHead {
  /** The tab and search-result title. */
  title: string;
  description?: string;
  /** The page's canonical path; omitted when the site origin is unknown. */
  canonicalPath?: string;
  /** Keep this page out of search results (a not-found page). */
  noindex?: boolean;
  jsonLd?: object[];
}

/** Marks the tags this module added, so it removes only its own. */
const OWNED = 'data-page-head';

/**
 * The origin canonical addresses point at: the host the build is served on
 * (`VITE_SITE_ORIGIN`, the same value that gates the snapshots' canonical).
 * Unset, no canonical is written — one naming a host that does not serve the
 * page would deindex it.
 */
export function siteOrigin(): string | null {
  const configured = import.meta.env.VITE_SITE_ORIGIN as string | undefined;
  return configured ? configured.replace(/\/+$/, '') : null;
}

/** A title that keeps the unread badge already on the tab. */
export function titleKeepingBadge(current: string, title: string): string {
  const badge = current.slice(0, current.length - stripTitleBadge(current).length);
  return `${badge}${title}`;
}

function addOwned<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, string>,
  text?: string,
): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  for (const [name, value] of Object.entries(attrs)) el.setAttribute(name, value);
  el.setAttribute(OWNED, '');
  if (text !== undefined) el.textContent = text;
  document.head.appendChild(el);
  return el;
}

/** Apply `head` to the document; returns the function that puts it back. */
export function applyPageHead(head: PageHead): () => void {
  const previousTitle = stripTitleBadge(document.title);
  document.title = titleKeepingBadge(document.title, head.title);

  const description = document.head.querySelector<HTMLMetaElement>('meta[name="description"]');
  const previousDescription = description?.getAttribute('content') ?? null;
  if (head.description !== undefined) {
    if (description) description.setAttribute('content', head.description);
    else addOwned('meta', { name: 'description', content: head.description });
  }

  const added: Element[] = [];
  const origin = siteOrigin();
  if (head.canonicalPath && origin) {
    added.push(addOwned('link', { rel: 'canonical', href: `${origin}${head.canonicalPath}` }));
  }
  if (head.noindex) {
    added.push(addOwned('meta', { name: 'robots', content: 'noindex' }));
  }
  for (const data of head.jsonLd ?? []) {
    added.push(addOwned('script', { type: 'application/ld+json' }, serializeJsonForScript(data)));
  }

  return () => {
    document.title = titleKeepingBadge(document.title, previousTitle);
    if (description && previousDescription !== null) {
      description.setAttribute('content', previousDescription);
    }
    for (const el of added) el.remove();
    document.head
      .querySelectorAll(`meta[name="description"][${OWNED}]`)
      .forEach((el) => el.remove());
  };
}

/** {@link applyPageHead} for as long as the calling component shows `head`. */
export function usePageHead(head: PageHead | null): void {
  // Keyed by content, so a re-render with the same page writes nothing.
  const key = head ? JSON.stringify(head) : '';
  useEffect(() => {
    if (!head) return;
    return applyPageHead(head);
    // `key` is `head`'s content: a new object with the same content is no change.
  }, [key]);
}
